import React from 'react';
import { useHistory } from 'react-router-dom';
import { Tooltip } from '@mui/material';

export const DELAYED_SCREENING_MESSAGE =
  'Delayed: this database is taking longer than usual';

export type DatabaseType = 'people & companies' | 'products' | 'tariffs';

export interface DatabaseCardStat {
  label: string;
  value: number;
  icon?: React.ReactNode;
}

export interface DatabaseCardProps {
  id: string;
  name: string;
  description?: string;
  type: DatabaseType;
  screeningRate?: number;
  openIssues?: number;
  lastUpdated?: string;
  isProcessing?: boolean;
  isScreeningDelayed?: boolean;
  stats?: [DatabaseCardStat, DatabaseCardStat, DatabaseCardStat];
  onClick?: () => void;
}

const IconUsers = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' />
    <circle cx='9' cy='7' r='4' />
    <path d='M22 21v-2a4 4 0 0 0-3-3.87' />
    <path d='M16 3.13a4 4 0 0 1 0 7.75' />
  </svg>
);

const IconPackage = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M16.5 9.4 7.55 4.24' />
    <path d='M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z' />
    <polyline points='3.29 7 12 12 20.71 7' />
    <line x1='12' x2='12' y1='22' y2='12' />
  </svg>
);

const IconFileText = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z' />
    <polyline points='14 2 14 8 20 8' />
    <line x1='16' x2='8' y1='13' y2='13' />
    <line x1='16' x2='8' y1='17' y2='17' />
    <line x1='10' x2='8' y1='9' y2='9' />
  </svg>
);

const IconCheckCircle2 = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z' />
    <path d='m9 12 2 2 4-4' />
  </svg>
);

const IconAlertTriangle = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z' />
    <path d='M12 9v4' />
    <path d='M12 17h.01' />
  </svg>
);

const IconAlertCircle = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <circle cx='12' cy='12' r='10' />
    <line x1='12' x2='12' y1='8' y2='12' />
    <line x1='12' x2='12.01' y1='16' y2='16' />
  </svg>
);

function MiniGauge({ isLoading }: { isLoading?: boolean }) {
  if (!isLoading) return null;

  const r = 10;
  const circumference = 2 * Math.PI * r;
  const arc = circumference * 0.75;

  return (
    <svg
      className='bdb-nc-gauge is-spinning'
      width={26}
      height={26}
      viewBox='0 0 26 26'
    >
      <circle
        cx='13'
        cy='13'
        r={r}
        fill='none'
        stroke='var(--bg-border-db, #e2e8f0)'
        strokeWidth='2.5'
      />
      <circle
        cx='13'
        cy='13'
        r={r}
        fill='none'
        stroke='var(--action-color, #4895ef)'
        strokeWidth='2.5'
        strokeLinecap='round'
        strokeDasharray={`${arc} ${circumference - arc}`}
      />
    </svg>
  );
}

function ComplianceBadge({ rate }: { rate: number }) {
  if (rate < 10) {
    return (
      <span className='bdb-nc-badge bdb-nc-badge-success'>
        <IconCheckCircle2 />
        {rate.toFixed(1)}%
      </span>
    );
  }
  if (rate <= 35) {
    return (
      <span className='bdb-nc-badge bdb-nc-badge-warning'>
        <IconAlertTriangle />
        {rate.toFixed(1)}%
      </span>
    );
  }
  return (
    <span className='bdb-nc-badge bdb-nc-badge-error'>
      <IconAlertCircle />
      {rate.toFixed(1)}%
    </span>
  );
}

function DatabaseTypeBadge({ type }: { type: DatabaseType }) {
  if (type === 'people & companies') {
    return (
      <span className='bdb-nc-badge bdb-nc-badge-type-entities'>
        <IconUsers />
        Entities
      </span>
    );
  }
  if (type === 'products') {
    return (
      <span className='bdb-nc-badge bdb-nc-badge-type-products'>
        <IconPackage />
        Products
      </span>
    );
  }
  return (
    <span className='bdb-nc-badge bdb-nc-badge-type-tariffs'>
      <IconFileText />
      Tariffs
    </span>
  );
}

function DBIcon({ type }: { type: DatabaseType }) {
  if (type === 'people & companies') return <IconUsers />;
  if (type === 'products') return <IconPackage />;
  return <IconFileText />;
}

const DatabaseCard: React.FC<DatabaseCardProps> = ({
  id,
  name,
  description,
  type,
  screeningRate = 0,
  openIssues = 0,
  lastUpdated,
  isProcessing = false,
  stats,
  onClick,
}) => {
  const history = useHistory();

  const handleClick = () => {
    if (isProcessing) return;
    if (onClick) {
      onClick();
    } else {
      history.push(`/database/${id}`);
    }
  };

  return (
    <div
      className='entity-db-card'
      onClick={handleClick}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
    >
      <div className='entity-db-card-head'>
        <div className='entity-db-card-icon'>
          <DBIcon type={type} />
        </div>
        <div className='entity-db-card-head-right'>
          <DatabaseTypeBadge type={type} />
          <MiniGauge isLoading={isProcessing} />
        </div>
      </div>

      <div className='entity-db-card-name' title={name}>
        {name}
      </div>
      <p className='entity-db-card-desc' title={description}>
        {description || 'No description'}
      </p>

      {stats && (
        <div className='entity-db-card-kpis'>
          {stats.map((stat, i) => (
            <div key={i} className='entity-db-kpi'>
              <span className='entity-db-kpi-value'>
                {stat.value.toLocaleString()}
              </span>
              <span className='entity-db-kpi-label'>{stat.label}</span>
            </div>
          ))}
        </div>
      )}

      <div className='entity-db-card-foot'>
        <Tooltip
          title='% of entities flagged as high risk and not cleared'
          placement='top'
          arrow
          enterDelay={700}
          enterNextDelay={700}
        >
          <span>
            <ComplianceBadge rate={screeningRate} />
          </span>
        </Tooltip>
        <span className='entity-db-card-foot-text'>
          {type === 'products'
            ? `${openIssues} open issue${openIssues !== 1 ? 's' : ''}`
            : lastUpdated ?? ''}
        </span>
      </div>

      {type === 'products' && lastUpdated && (
        <p className='entity-db-card-foot-updated'>{lastUpdated}</p>
      )}
    </div>
  );
};

export default DatabaseCard;
